import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CVPreview from './CVPreview';
import { CVData } from '../../types/cv';
import {
    A4_PAGE_HEIGHT_PX,
    A4_PAGE_WIDTH_PX,
    buildPageData,
    compactPageCount,
    ensureCVIdsAndLayout,
    findOverflowCandidate,
    isMainFlowMonotonic,
    isSidebarFlowMonotonic,
    moveItemToPage,
    pageHasContent,
    resetMainFlowToFirstPage,
} from '../../utils/cvPagination';
import { API_URL } from '../../services/api';

/** Convertit le document Mongo / API en CVData + template. */
function mapApiCv(doc: any): { data: CVData; template: string } {
    const template = String(doc.template || 'ocean-wave');
    const data = ensureCVIdsAndLayout({
        title: doc.title || 'CV',
        personalInfo: {
            firstName: doc.personalInfo?.firstName || '',
            lastName: doc.personalInfo?.lastName || '',
            email: doc.personalInfo?.email || '',
            phone: doc.personalInfo?.phone || '',
            address: doc.personalInfo?.address || '',
            city: doc.personalInfo?.city || '',
            country: doc.personalInfo?.country || '',
            profileImage: doc.personalInfo?.profileImage,
            profession: doc.personalInfo?.profession || '',
            summary: doc.personalInfo?.summary || '',
            website: doc.personalInfo?.website,
        },
        education: doc.education || [],
        experience: doc.experience || [],
        skills: doc.skills || [],
        languages: doc.languages || [],
        interests: doc.interests || [],
        references: doc.references || [],
        style: doc.style || {},
        layout: doc.layout || { pageCount: 1, itemPages: {} },
    });
    return { data, template };
}

async function waitForImages(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
        imgs.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete) return resolve();
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    setTimeout(resolve, 8000);
                })
        )
    );
}

const OVERFLOW_TOLERANCE_PX = 14;
/** Borne dure : évite une boucle infinie si un bloc unique dépasse déjà une page pleine à lui seul. */
const MAX_CORRECTION_PASSES = 60;

/**
 * Page minimale pour Puppeteer — pas de chrome builder.
 * Auth via ?ticket= JWT court (GET /api/cv/print-data).
 *
 * Filet de sécurité anti-troncature : rejoue ici la même correction de pagination
 * que l'éditeur (déplacer le surplus qui dépasse une page vers la suivante),
 * indépendamment de ce qui a été sauvegardé — le PDF ne doit jamais couper du
 * contenu qui aurait eu de la place sur une page suivante.
 */
const PrintCvPage: React.FC = () => {
    const { cvId } = useParams<{ cvId: string }>();
    const [search] = useSearchParams();
    const ticket = search.get('ticket') || '';
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CVData | null>(null);
    const [template, setTemplate] = useState<string>('ocean-wave');
    const [passCount, setPassCount] = useState(0);
    const [paginationStable, setPaginationStable] = useState(false);
    const [assetsReady, setAssetsReady] = useState(false);

    useEffect(() => {
        document.body.classList.add('print-cv-mode');
        return () => document.body.classList.remove('print-cv-mode');
    }, []);

    useEffect(() => {
        if (!cvId || !ticket) {
            setError('Ticket d’impression manquant');
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${API_URL}/cv/print-data?ticket=${encodeURIComponent(ticket)}`
                );
                const json = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(json.error || 'Chargement CV impossible');
                if (String(json._id) !== String(cvId)) {
                    throw new Error('CV / ticket incohérents');
                }
                if (cancelled) return;
                const mapped = mapApiCv(json);
                setData(mapped.data);
                setTemplate(mapped.template);
            } catch (e: any) {
                if (!cancelled) setError(e.message || 'Erreur');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cvId, ticket]);

    /**
     * Filet de sécurité anti-troncature : après chaque rendu, mesure le débordement
     * réel des colonnes et déplace le surplus vers la page suivante — jusqu'à
     * stabilisation. Reproduit la logique de BuilderPreview, en plus simple
     * (push uniquement, pas de remontée) pour ne jamais rien couper.
     */
    useEffect(() => {
        if (!data || paginationStable) return;
        if (passCount >= MAX_CORRECTION_PASSES) {
            setPaginationStable(true);
            return;
        }

        const timer = window.setTimeout(() => {
            requestAnimationFrame(() => {
                if (!isMainFlowMonotonic(data, template) || !isSidebarFlowMonotonic(data, template)) {
                    setData(resetMainFlowToFirstPage(data, template));
                    setPassCount((n) => n + 1);
                    return;
                }

                const pageNodes = Array.from(
                    document.querySelectorAll<HTMLElement>('#print-cv-root [data-cv-page]')
                );
                if (pageNodes.length === 0) {
                    setPaginationStable(true);
                    return;
                }

                const columnLimit = (col: HTMLElement) => {
                    const colBottom = col.getBoundingClientRect().bottom;
                    const sheet = col.closest('.cv-page-sheet') as HTMLElement | null;
                    if (!sheet) return colBottom;
                    const sr = sheet.getBoundingClientRect();
                    return Math.min(colBottom, sr.bottom - Math.max(28, sr.height * 0.04));
                };

                const colOverflows = (col: HTMLElement | null) => {
                    if (!col) return false;
                    const limit = columnLimit(col);
                    const blocks = col.querySelectorAll<HTMLElement>('[data-cv-block]');
                    if (blocks.length === 0) {
                        return (
                            col.scrollHeight > col.clientHeight + OVERFLOW_TOLERANCE_PX &&
                            col.clientHeight > 40
                        );
                    }
                    for (const el of Array.from(blocks)) {
                        const r = el.getBoundingClientRect();
                        if (r.height < 4) continue;
                        if (r.bottom > limit + OVERFLOW_TOLERANCE_PX) return true;
                    }
                    return false;
                };

                const readPageIndex = (host: HTMLElement) => {
                    const raw = host.getAttribute('data-cv-page');
                    const n = raw == null ? NaN : Number(raw);
                    return Number.isFinite(n) ? n : null;
                };

                const sheetOf = (host: HTMLElement) =>
                    (host.querySelector('.cv-page-sheet') as HTMLElement | null) || host;

                for (const host of pageNodes) {
                    const pageIndex = readPageIndex(host);
                    if (pageIndex == null) continue;
                    const sheet = sheetOf(host);
                    const sidebar = sheet.querySelector<HTMLElement>('[data-cv-sidebar]');
                    const main = sheet.querySelector<HTMLElement>('[data-cv-main]');

                    if (main && colOverflows(main)) {
                        const candidate = findOverflowCandidate(data, pageIndex, 'main', template);
                        if (candidate) {
                            setData(compactPageCount(moveItemToPage(data, candidate.itemId, pageIndex + 1)));
                            setPassCount((n) => n + 1);
                            return;
                        }
                    }
                    if (sidebar && colOverflows(sidebar)) {
                        const candidate = findOverflowCandidate(data, pageIndex, 'sidebar', template);
                        if (candidate) {
                            setData(compactPageCount(moveItemToPage(data, candidate.itemId, pageIndex + 1)));
                            setPassCount((n) => n + 1);
                            return;
                        }
                    }
                }

                // Plus aucun débordement détecté → mise en page stable.
                setPaginationStable(true);
            });
        }, 30);
        return () => window.clearTimeout(timer);
    }, [data, template, passCount, paginationStable]);

    useEffect(() => {
        if (!data || !paginationStable || assetsReady) return;
        let cancelled = false;
        (async () => {
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
            const root = document.getElementById('print-cv-root');
            if (root) await waitForImages(root);
            try {
                if (document.fonts?.ready) await document.fonts.ready;
            } catch {
                /* ignore */
            }
            await new Promise((r) => setTimeout(r, 200));
            if (!cancelled) setAssetsReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [data, paginationStable, assetsReady]);

    if (error) {
        return (
            <div data-print-ready="false" style={{ padding: 24, fontFamily: 'sans-serif' }}>
                {error}
            </div>
        );
    }

    if (!data) {
        return (
            <div data-print-ready="false" style={{ padding: 24 }}>
                Chargement…
            </div>
        );
    }

    const ready = paginationStable && assetsReady;
    const pageCount = Math.max(1, data.layout?.pageCount || 1);
    const pages: number[] = [];
    for (let i = 0; i < pageCount; i++) {
        if (i === 0 || pageHasContent(data, i)) pages.push(i);
    }

    return (
        <div
            id="print-cv-root"
            data-print-ready={ready ? 'true' : 'false'}
            style={{
                margin: 0,
                padding: 0,
                background: '#fff',
                width: A4_PAGE_WIDTH_PX,
            }}
        >
            {pages.map((pageIndex) => {
                const pageData = buildPageData(data, pageIndex);
                return (
                    <div
                        key={pageIndex}
                        data-cv-page={pageIndex}
                        style={{
                            width: A4_PAGE_WIDTH_PX,
                            height: A4_PAGE_HEIGHT_PX,
                            overflow: 'hidden',
                            pageBreakAfter: 'always',
                            breakAfter: 'page',
                        }}
                    >
                        <div style={{ width: '210mm', height: '297mm' }}>
                            <CVPreview
                                data={pageData}
                                fullData={data}
                                template={template}
                                isContinuationPage={pageIndex > 0}
                                pageIndex={pageIndex}
                                totalPages={pages.length}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PrintCvPage;
